// ============================================
// Radial Zoom Blur Post-Process Effect
// ============================================
// Anime speed-lines / hyperdrive: dithered radial streaks toward a focus
// point with a protected sharp center.

import type { IRenderContext } from '../../core/IRenderContext';
import {
  PostProcessEffect,
  type PostProcessContext,
  type PostProcessParameter,
} from '../PostProcessStack';
import { ZOOM_BLUR_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Zoom (radial) blur post-processing effect
 */
export class ZoomBlurEffect extends PostProcessEffect {
  readonly type = 'zoomBlur';
  readonly displayName = 'Radial Blur';

  // Parameters
  centerX = 0.5;
  centerY = 0.5;
  strength = 0.3;
  samples = 16;
  startRadius = 0.15;
  dither = 1.0;

  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = ZOOM_BLUR_LAYOUT.createWriter();

  getParameters(): PostProcessParameter[] {
    return [
      { name: 'centerX', type: 'float', min: 0, max: 1, default: 0.5, description: 'Focus X' },
      { name: 'centerY', type: 'float', min: 0, max: 1, default: 0.5, description: 'Focus Y' },
      { name: 'strength', type: 'float', min: 0, max: 1, default: 0.3, description: 'Blur reach' },
      { name: 'samples', type: 'int', min: 4, max: 32, default: 16, description: 'Samples' },
      { name: 'startRadius', type: 'float', min: 0, max: 1, default: 0.15, description: 'Sharp center radius' },
      { name: 'dither', type: 'float', min: 0, max: 1, default: 1.0, description: 'Banding dither' },
    ];
  }

  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'centerX': return this.centerX;
      case 'centerY': return this.centerY;
      case 'strength': return this.strength;
      case 'samples': return this.samples;
      case 'startRadius': return this.startRadius;
      case 'dither': return this.dither;
      default: return 0;
    }
  }

  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'centerX': this.centerX = value; break;
      case 'centerY': this.centerY = value; break;
      case 'strength': this.strength = Math.max(0, Math.min(1, value)); break;
      case 'samples': this.samples = Math.floor(Math.max(4, Math.min(32, value))); break;
      case 'startRadius': this.startRadius = Math.max(0, Math.min(1, value)); break;
      case 'dither': this.dither = Math.max(0, Math.min(1, value)); break;
    }
  }

  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();

    const zoomBlurWgsl = getCompiledShader('postprocess/zoom-blur');
    this.shader = ctx.createShader(zoomBlurWgsl, zoomBlurWgsl);

    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'zoom-blur-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'zoom-blur-ldr',
    });
  }

  protected onRender(context: PostProcessContext): void {
    const { ctx, source, width, height, intensity, isHdrTarget } = context;

    // NOTE: PostProcessStack already set render target and viewport

    this.uniformWriter.setAll({
      center: [this.centerX, this.centerY],
      strength: this.strength * intensity,
      samples: this.samples,
      startRadius: this.startRadius,
      dither: this.dither,
      aspect: width / Math.max(height, 1),
      _pad: 0,
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }

  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
